import React, { useState, useEffect } from "react";
import { FaPlus, FaTrash, FaCheck, FaTimes, FaEdit } from "react-icons/fa";
import toast from "react-hot-toast";
import api from "../../../services/api";

export default function TodoList({ user }) {
  const [todos, setTodos] = useState([]);
  const [showAddTask, setShowAddTask] = useState(false);
  const [newTaskName, setNewTaskName] = useState("");
  const [newTaskDescription, setNewTaskDescription] = useState("");
  const [newTaskDeadline, setNewTaskDeadline] = useState("");
  const [editingTaskId, setEditingTaskId] = useState(null);

  const fetchTodos = async () => {
    try {
      const { data, ok } = await api.post("/mgt_task/search", {
        user_id: user._id,
      });
      if (!ok) return toast.error("Error fetching todos");
      setTodos(data);
    } catch (error) {
      toast.error("Error fetching todos");
    }
  };

  useEffect(() => {
    fetchTodos();
  }, [user]);

  const addTodo = async () => {
    if (!newTaskName.trim()) {
      toast.error("Please fill in task name");
      return;
    }

    try {
      const { data, ok } = await api.post("/mgt_task", {
        user_id: user._id,
        user_name: user.name,
        user_avatar: user.avatar,
        name: newTaskName.trim(),
        description: newTaskDescription.trim(),
        deadline: newTaskDeadline || null,
        completed: false,
      });
      if (!ok) return toast.error("Error adding task");
      setTodos([data, ...todos]);
      setNewTaskName("");
      setNewTaskDescription("");
      setNewTaskDeadline("");
      setShowAddTask(false);
      toast.success("Task added successfully");
    } catch (error) {
      toast.error("Error adding task");
    }
  };

  const startEditTask = (task) => {
    setEditingTaskId(task._id);
    setNewTaskName(task.name || "");
    setNewTaskDescription(task.description || "");
    setNewTaskDeadline(task.deadline ? task.deadline.split('T')[0] : "");
    setShowAddTask(true);
  };

  const submitEditTask = async () => {
    if (!editingTaskId) return;
    if (!newTaskName.trim()) {
      toast.error("Please fill in task name");
      return;
    }

    const updates = {
      name: newTaskName.trim(),
      description: newTaskDescription.trim(),
      deadline: newTaskDeadline || null,
    };

    await updateTask(editingTaskId, updates);

    setEditingTaskId(null);
    setNewTaskName("");
    setNewTaskDescription("");
    setNewTaskDeadline("");
    setShowAddTask(false);
    fetchTodos();
  };

  const updateTask = async (taskId, updates) => {
    try {
      const { data, ok } = await api.put(`/mgt_task/${taskId}`, updates);
      if (!ok) return toast.error("Error updating task");
      setTodos((prev) => prev.map((task) => (task._id === taskId ? { ...task, ...data } : task)));
      toast.success("Task updated successfully");
    } catch (error) {
      toast.error("Error updating task");
    }
  };

  const toggleTodo = async (todo) => {
    try {
      const { ok } = await api.put(`/mgt_task/${todo._id}`, { completed: !todo.completed });
      if (!ok) return toast.error("Error updating task");
      setTodos(todos.map((t) => (t._id === todo._id ? { ...t, completed: !t.completed } : t)));
      toast.success("Task updated");
    } catch (error) {
      toast.error("Error updating task");
    }
  };

  const deleteTodo = async (todo) => {
    try {
      const { ok } = await api.remove(`/mgt_task/${todo._id}`);
      if (!ok) return toast.error("Error deleting task");
      setTodos(todos.filter((t) => t._id !== todo._id));
      toast.success("Task deleted");
    } catch (error) {
      toast.error("Error deleting task");
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-xl font-semibold text-gray-900">Management Tasks</h3>
        <button
          onClick={() => setShowAddTask(!showAddTask)}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors">
          <FaPlus className="w-3 h-3" />
          Add Task
        </button>
      </div>

      {showAddTask && (
        <div className="mb-6 p-4 bg-gray-50 rounded-lg border">
          <div className="grid grid-cols-1 gap-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Task Name *</label>
                <input
                  type="text"
                  value={newTaskName}
                  onChange={(e) => setNewTaskName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter task name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Deadline</label>
                <input
                  type="date"
                  value={newTaskDeadline}
                  onChange={(e) => setNewTaskDeadline(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <textarea
                value={newTaskDescription}
                onChange={(e) => setNewTaskDescription(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter task description (optional)"
                rows={2}
              />
            </div>

            <div className="flex items-end gap-2">
              <button
                onClick={editingTaskId ? submitEditTask : addTodo}
                className="px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-md hover:bg-green-700 transition-colors">
                <FaCheck className="w-3 h-3 inline mr-1" />
                {editingTaskId ? "Save changes" : "Add Task"}
              </button>
              <button
                onClick={() => {
                  setShowAddTask(false);
                  setNewTaskName("");
                  setNewTaskDescription("");
                  setNewTaskDeadline("");
                  setEditingTaskId(null);
                }}
                className="px-4 py-2 bg-gray-300 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-400 transition-colors">
                <FaTimes className="w-3 h-3 inline mr-1" />
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-2">
        {todos.map((todo) => (
          <div key={todo._id} className={`flex items-start gap-3 p-4 rounded-lg border ${todo.completed ? "bg-gray-50 border-gray-200" : "bg-white border-gray-300"}`}>
            <button
              onClick={() => toggleTodo(todo)}
              className={`flex-shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center mt-1 ${
                todo.completed ? "bg-green-500 border-green-500 text-white" : "border-gray-300 hover:border-green-500"
              }`}>
              {todo.completed && <FaCheck className="w-3 h-3" />}
            </button>
            <div className="flex-1">
              <div className={`font-medium ${todo.completed ? "text-gray-500 line-through" : "text-gray-900"}`}>
                {todo.name}
              </div>
              {todo.description && (
                <div className={`text-sm mt-1 ${todo.completed ? "text-gray-400" : "text-gray-600"}`}>
                  {todo.description}
                </div>
              )}
              {todo.deadline && (
                <div className={`text-xs mt-1 ${todo.completed ? "text-gray-400" : "text-gray-500"}`}>
                  Deadline: {formatDate(todo.deadline)}
                </div>
              )}
            </div>
            <div className="flex gap-1">
              <button onClick={() => startEditTask(todo)} className="p-1 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded" title="Edit Task">
                <FaEdit className="w-3 h-3" />
              </button>
              <button onClick={() => deleteTodo(todo)} className="p-1 text-red-600 hover:text-red-800 hover:bg-red-50 rounded" title="Delete Task">
                <FaTrash className="w-3 h-3" />
              </button>
            </div>
          </div>
        ))}
      </div>

      {todos.length === 0 && (
        <div className="text-center py-8 text-gray-500">
          <p>No tasks yet. Add one above!</p>
        </div>
      )}
    </div>
  );
}
